import { useState } from "react";
import { toast } from "sonner";
import { apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileUp, Loader2, Sparkles, ArrowLeft } from "lucide-react";

function DraftDay({ d, base, setField }) {
  return (
    <div className="rounded-2xl border-2 border-ink p-4">
      <Input value={d.title || ""} onChange={(e) => setField([...base, "title"], e.target.value)} className="border-2 border-ink h-9 rounded-lg font-bold mb-2" placeholder="Day title" />
      <Input value={d.scripture_reference || ""} onChange={(e) => setField([...base, "scripture_reference"], e.target.value)} className="border-2 border-ink h-9 rounded-lg mb-2 text-sm" placeholder="Scripture reference" />
      <Textarea value={d.devotional || ""} onChange={(e) => setField([...base, "devotional"], e.target.value)} rows={3} className="border-2 border-ink rounded-lg resize-none text-sm mb-2" placeholder="Devotional" />
      <div className="text-[11px] font-bold uppercase text-muted-foreground mb-1">Pre-reading questions</div>
      {(d.pre_reading_questions || []).map((q, qi) => (
        <Input key={`${d._k}-pre-${qi}`} value={q} onChange={(e) => setField([...base, "pre_reading_questions", qi], e.target.value)} className="border-2 border-ink h-9 rounded-lg mb-1 text-sm" />
      ))}
      <div className="text-[11px] font-bold uppercase text-muted-foreground mb-1 mt-2">Reflection questions</div>
      {(d.reflection_questions || []).map((q, qi) => (
        <Input key={`${d._k}-ref-${qi}`} value={q} onChange={(e) => setField([...base, "reflection_questions", qi], e.target.value)} className="border-2 border-ink h-9 rounded-lg mb-1 text-sm" />
      ))}
    </div>
  );
}

function DraftEditor({ draft, setField, busy, onPublish, onDiscard }) {
  return (
    <div className="max-w-3xl" data-testid="import-preview">
      <button onClick={onDiscard} className="flex items-center gap-1 text-sm font-bold mb-4 press"><ArrowLeft size={15} /> Discard draft</button>
      <div className="rounded-3xl border-2 border-ink bg-white shadow-pop p-6 mb-4">
        <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">AI draft · {draft._counts?.weeks} weeks · {draft._counts?.days} days · {draft._counts?.questions} questions</div>
        <Label>Class title</Label>
        <Input value={draft.title || ""} onChange={(e) => setField(["title"], e.target.value)} className="border-2 border-ink h-11 rounded-xl mt-1 mb-3 font-bold" data-testid="draft-title" />
        <Label>Description</Label>
        <Textarea value={draft.description || ""} onChange={(e) => setField(["description"], e.target.value)} rows={2} className="border-2 border-ink rounded-xl mt-1 resize-none" data-testid="draft-description" />
      </div>

      <div className="space-y-4">
        {(draft.weeks || []).map((w, wi) => (
          <div key={w._k} className="rounded-3xl border-2 border-ink bg-white overflow-hidden">
            <div className="px-5 py-3 border-b-2 border-ink flex items-center gap-2" style={{ background: "#FFFDF5" }}>
              <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Week {wi + 1}</span>
              <Input value={w.title || ""} onChange={(e) => setField(["weeks", wi, "title"], e.target.value)} className="border-2 border-ink h-9 rounded-lg font-bold" />
            </div>
            <div className="p-5 space-y-4">
              {(w.days || []).map((d, di) => <DraftDay key={d._k} d={d} base={["weeks", wi, "days", di]} setField={setField} />)}
            </div>
          </div>
        ))}
      </div>

      <Button onClick={onPublish} disabled={busy} data-testid="draft-publish" className="w-full h-12 mt-5 rounded-full border-2 border-ink shadow-pop press font-bold">
        {busy ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Publishing…</>) : "Publish class"}
      </Button>
    </div>
  );
}

export function ImportTab() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [result, setResult] = useState(null);

  const analyze = async () => {
    if (!file) return toast.error("Choose a PDF first");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const data = await apiPost("/admin/import-class/preview", fd);
      let seq = 0;
      (data.weeks || []).forEach((w) => {
        w._k = `w${seq++}`;
        (w.days || []).forEach((d) => { d._k = `d${seq++}`; });
      });
      setDraft(data);
      toast.success("Draft ready — review and edit before publishing");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { _counts, ...payload } = draft;
      const res = await apiPost("/admin/import-class/commit", payload);
      setResult(res); setDraft(null); setFile(null);
      toast.success(`Published "${res.title}" — ${res.weeks} weeks, ${res.days} days`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const setField = (path, value) => setDraft((d) => {
    const copy = JSON.parse(JSON.stringify(d));
    let ref = copy;
    for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
    ref[path[path.length - 1]] = value;
    return copy;
  });

  if (draft) return <DraftEditor draft={draft} setField={setField} busy={busy} onPublish={publish} onDiscard={() => setDraft(null)} />;

  return (
    <div className="max-w-2xl" data-testid="import-tab">
      <div className="rounded-3xl border-2 border-ink bg-white shadow-pop p-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={20} style={{ color: "#FF5722" }} />
          <h3 className="font-extrabold text-xl">Import a class from PDF</h3>
        </div>
        <p className="text-muted-foreground mb-6 text-sm">
          Upload a curriculum PDF (like "The Story"). Claude Sonnet 5.5 reads it and drafts
          the full class — weeks, days, scriptures, devotionals and questions. You review and
          edit the draft, then publish.
        </p>

        <label className="block rounded-2xl border-2 border-dashed border-ink p-8 text-center cursor-pointer press" style={{ background: "#FFFDF5" }} data-testid="import-dropzone">
          <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="import-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <FileUp size={28} className="mx-auto mb-2" />
          <div className="font-bold">{file ? file.name : "Click to choose a PDF"}</div>
          <div className="text-xs text-muted-foreground mt-1">Max 15 MB · text-based PDFs work best</div>
        </label>

        <Button onClick={analyze} disabled={busy || !file} data-testid="import-submit" className="w-full h-12 mt-5 rounded-full border-2 border-ink shadow-pop press font-bold disabled:opacity-50">
          {busy ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing… (10-30s)</>) : "Analyze & preview"}
        </Button>

        {result && (
          <div className="mt-5 rounded-2xl border-2 border-ink p-4" style={{ background: "#B8FF3B" }} data-testid="import-result">
            <div className="font-extrabold">✓ {result.title}</div>
            <div className="text-sm font-semibold">{result.weeks} weeks · {result.days} days · {result.questions} questions</div>
            <div className="text-xs mt-1">The class is now live for students (unlocks after prior classes).</div>
          </div>
        )}
      </div>
    </div>
  );
}
